/**
 * THE OWNERSHIP CERTIFICATE. UF-05 section 5.7, FR-U-TOK-003.
 *
 * The single most important thing on this screen is a sentence that says what the holder does NOT
 * own.
 *
 * A certificate that said "you own this land" would be telling the holder something untrue. They own
 * an interest in a legal entity, and that entity is what the deed names as owner. The distinction is
 * the entire legal foundation of the product (ADR 0005): get it wrong on this screen and every holder
 * on the platform has been told something false about their own property, in writing, by us.
 *
 * So the backend supplies `what_you_own` as a sentence, and it is rendered VERBATIM. It is not
 * paraphrased here, because a paraphrase is where the meaning would quietly go.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type TokenDetail } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useSession } from '@/lib/session-context';

export default function TokenPage() {
  // React Router types a param as possibly absent; this route cannot match without one.
  const { tokenId = '' } = useParams();
  const { me } = useSession();

  const [token, setToken] = useState<TokenDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setToken(await api.token(tokenId));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this token.');
    } finally {
      setLoading(false);
    }
  }, [tokenId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingCard lines={6} />;
  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!token) return null;

  const mine = token.holders.find((h) => h.holder_id === me?.participant_id);
  const pledged = token.state === 'locked';

  return (
    <div className="mx-auto max-w-md space-y-5">
      <div>
        <Link to="/tokenize" className="text-sm text-brand hover:underline">
          ← My assets
        </Link>
        <h1 className="mt-2 text-page-title text-ink">Ownership certificate</h1>
      </div>

      {/* WHAT YOU OWN. Verbatim from the backend. This is the sentence the whole design turns on. */}
      <section className="rounded-card border border-primary/20 bg-primary/5 p-5">
        <p className="text-xs font-medium uppercase tracking-wide text-brand">What you own</p>

        {mine ? (
          <p className="mt-2 text-2xl font-semibold text-ink">
            {(mine.fraction_bps / 100).toFixed(2)}%
          </p>
        ) : null}

        <p className="mt-2 text-sm leading-relaxed text-ink">{token.what_you_own}</p>
      </section>

      {token.legal_entity ? (
        <section className="rounded-card border border-border bg-surface p-5">
          <h2 className="text-sm font-medium text-ink">The company that holds it</h2>

          <dl className="mt-3 space-y-2 text-sm">
            <Row label="Name" value={token.legal_entity.legal_name} />
            <Row label="Type" value={token.legal_entity.kind.replace(/_/g, ' ')} />
            <Row label="Registration" value={token.legal_entity.registration_number} mono />
            <Row label="Jurisdiction" value={token.legal_entity.jurisdiction} />
          </dl>
        </section>
      ) : null}

      <section className="rounded-card border border-border bg-surface p-5">
        <h2 className="text-sm font-medium text-ink">The asset</h2>

        <dl className="mt-3 space-y-2 text-sm">
          <Row label="Type" value={token.asset.class.replace(/_/g, ' ')} />
          <Row label="Reference" value={token.asset.ownership_reference} mono />
          <Row label="Classification" value={token.classification.replace(/_/g, ' ')} />
          <Row label="State" value={token.state} />
          <Row label="Issued" value={new Date(token.minted_at).toLocaleDateString()} />
        </dl>
      </section>

      {/* THE VALUATION. This is the figure a loan would be measured against, so the owner sees it. */}
      {token.current_valuation ? (
        <section className="rounded-card border border-border bg-surface p-5">
          <h2 className="text-sm font-medium text-ink">Reference valuation</h2>

          <p className="tabular mt-2 text-xl font-semibold text-ink">
            {token.current_valuation.asset_code} {money(token.current_valuation.amount)}
          </p>

          <p className="mt-1 text-xs text-ink-muted">
            {token.current_valuation.method.replace(/_/g, ' ')} · from{' '}
            {new Date(token.current_valuation.effective_from).toLocaleDateString()}
          </p>

          <p className="mt-3 text-xs leading-relaxed text-ink-muted">
            Confirmed by a different officer from the one who approved the token. If you borrow
            against this, it is the number your loan is measured against.
          </p>
        </section>
      ) : null}

      {/* THE DUAL-LEDGER RECONCILIATION, shown to the person it protects.

          Odinet & Tosato: a tokenized conveyance "would generate two separate records" — the token and
          the land register — and they can disagree. The register is authoritative and we are not it.
          This says which register we checked, and what it said. */}
      {token.title_verified_against ? (
        <section className="rounded-card border border-border bg-surface p-5">
          <h2 className="text-sm font-medium text-ink">We checked the register</h2>

          <dl className="mt-3 space-y-2 text-sm">
            <Row label="Register" value={token.title_verified_against.registry} />
            <Row
              label="Recorded owner"
              value={token.title_verified_against.registered_owner ?? '—'}
            />
            <Row
              label="Checked"
              value={new Date(token.title_verified_against.checked_at).toLocaleDateString()}
            />
          </dl>

          <p className="mt-3 text-xs leading-relaxed text-ink-muted">
            The register is the authority on who owns this, not us. We checked it and it agreed
            before this token was issued.
          </p>
        </section>
      ) : null}

      {pledged ? (
        <section className="rounded-card border border-warning/30 bg-warning/5 p-4">
          <p className="text-sm font-medium text-ink">This token is pledged as collateral</p>
          <p className="mt-1 text-sm text-ink-muted">
            It cannot be sold, transferred, or pledged again until the loan is repaid. That lock is
            what makes it worth something to a lender.
          </p>
        </section>
      ) : null}

      {/* HOLDERS. If somebody else holds a fraction, the owner sees it: FR-X-TOK-005 makes the
          registry the single source of truth for who owns what, and hiding it from the people it is
          about would defeat the point of having one. */}
      {token.holders.length > 1 ? (
        <section className="rounded-card border border-border bg-surface p-5">
          <h2 className="text-sm font-medium text-ink">Who holds this</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {token.holders.map((h) => (
              <li key={h.holder_id} className="flex justify-between gap-3">
                <span className="truncate font-mono text-xs text-ink-muted">
                  {h.holder_id === me?.participant_id ? 'You' : h.holder_id}
                </span>
                <span className="tabular shrink-0 font-medium text-ink">
                  {(h.fraction_bps / 100).toFixed(2)}%
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/*
        LIST FOR SALE, BUY A FRACTION, DE-TOKENIZE.

        UF-05 sections 5.8 to 5.11, all Phase 3. The phase is not open, and the backend has no
        endpoints for any of them.

        UF_05 section 7 is explicit about how to handle that: a phase that is not active shows as
        "available in a later phase", NEVER as broken. A greyed-out button that does nothing when
        pressed is the worst of both worlds.
      */}
      <p className="rounded-card border border-border bg-surface p-4 text-center text-xs leading-relaxed text-ink-muted">
        Selling part of this, and redeeming it back into the asset, open in a later phase of the
        sandbox. The Capital Markets Authority approves each phase before it opens.
      </p>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      <dd className={`truncate text-right font-medium text-ink ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

/** Minor units to a display string. Integer arithmetic only: a valuation drives loan-to-value. */
function money(minor: string): string {
  const v = BigInt(minor);
  const whole = v / 100n;
  const cents = v % 100n;

  return `${whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${cents
    .toString()
    .padStart(2, '0')}`;
}
